import { Box, Popover, useTheme } from "@mui/material";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { SelectionButton } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import {
  type BaseTableProps,
  FilesTable,
} from "@/shared/components/SearchResultViews";
import {
  TablePageState,
  TablePageAction,
} from "@/shared/components/SearchResultViews/stateReducer";
import { FileRecord } from "@/shared/domain/files";
import { FileNode } from "@/shared/domain/files/filesystem";

import { FileGrid } from "./FileGrid";
import ResultsViewToggle from "./ResultsViewToggle";
import { ResultsView } from "./types";

const noop = () => {};

export interface FileResultsProps extends BaseTableProps<FileRecord> {
  extraHeight: number;
  fileTableState: TablePageState;
  fileTableDispatch: React.Dispatch<TablePageAction>;
}

export const FileResults: React.FC<FileResultsProps> = ({
  loading,
  data: files,
  dataCount,
  extraHeight,
  fetchMoreData,
  fileTableState,
  fileTableDispatch,
}) => {
  const theme = useTheme();
  const { currentOrganization } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiles, setSelectedFiles] = React.useState<FileNode[]>([]);

  const view = (searchParams.get("view") as ResultsView) || ResultsView.Table;

  const handleViewChange = (newView: ResultsView) => {
    searchParams.set("view", newView);
    setSearchParams(searchParams);
  };

  const maxTableHeight = `calc(100vh - ${
    theme.navTopBarHeight
  } - 56px - 35px - ${parseInt(theme.spacing(3))}px) - 49px - ${extraHeight}px - 36px - ${parseInt(theme.spacing(2))}px`;

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleCollectionPopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    overrideAnchorEl?: HTMLElement | null,
  ) => {
    if (overrideAnchorEl) {
      setAnchorEl(overrideAnchorEl);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCollectionPopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          mt: theme.spacing(2),
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#FFF",
        }}
      >
        <ResultsViewToggle view={view} setView={handleViewChange} />
        <SelectionButton
          selectedItems={selectedFiles}
          handleDeselectAll={() => {
            setSelectedFiles([]);
          }}
          currentOrganization={currentOrganization}
          handleCollectionPopoverOpen={handleCollectionPopoverOpen}
        />
      </Box>
      {view === ResultsView.Table && (
        <FilesTable
          data={files}
          dataCount={dataCount}
          loading={loading}
          // TODO: Enable selectedRows and setSelectedRows in the table by
          // aligning how the SelectionButton and RobotoDataGrid work.
          selectedRows={undefined}
          setSelectedRows={noop}
          disableSelection={true}
          onRowSingleClick={noop}
          containerStyle={{
            transition: "max-width 0.3s ease-in-out",
            maxHeight: maxTableHeight,
          }}
          fetchMoreData={fetchMoreData}
          fileTableState={fileTableState}
          fileTableDispatch={fileTableDispatch}
        />
      )}
      {view === ResultsView.Grid && (
        <Box mt={theme.spacing(2)}>
          <FileGrid
            files={files}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            fetchMoreData={fetchMoreData}
          />
        </Box>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCollectionPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: "left",
        }}
        marginThreshold={5}
      >
        <AddToCollection
          selectedFiles={selectedFiles}
          close={handleCollectionPopoverClose}
        />
      </Popover>
    </>
  );
};
